import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { GA_CONFIG, isGAEnabled } from '../../config/analytics';
import { trackPageView } from '../../utils/analytics';

/**
 * Google Analytics Component
 * Handles Google Analytics initialization and page view tracking
 */
const GoogleAnalytics = () => {
  const location = useLocation();

  // Initialize Google Analytics on mount
  useEffect(() => {
    if (!isGAEnabled()) {
      if (GA_CONFIG.debug) {
        console.log('Google Analytics: Not enabled (no Measurement ID)');
      }
      return;
    }

    // Initialize dataLayer and gtag function first
    window.dataLayer = window.dataLayer || [];
    function gtag(...args) {
      window.dataLayer.push(args);
    }
    window.gtag = gtag;

    // Load Google Analytics script and wait for it to load
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_CONFIG.measurementId}`;
    
    // Wait for script to load before initializing
    script.onload = () => {
      gtag('js', new Date());
      gtag('config', GA_CONFIG.measurementId, {
        page_path: window.location.pathname,
        page_title: document.title,
      });

      if (GA_CONFIG.debug) {
        console.log('Google Analytics: Initialized', GA_CONFIG.measurementId);
      }
    };

    document.head.appendChild(script);

    // Cleanup function
    return () => {
      // Remove script if component unmounts (though this is unlikely)
      const scripts = document.querySelectorAll(`script[src*="googletagmanager.com"]`);
      scripts.forEach((script) => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, []); // Only run once on mount

  // Track page views on route changes (React Router)
  useEffect(() => {
    if (!isGAEnabled()) {
      return;
    }

    // Small delay to ensure gtag is ready and title is updated
    const timeoutId = setTimeout(() => {
      const path = location.pathname + location.search;
      const title = document.title || 'Healix Medical';
      trackPageView(path, title);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.search]);

  // This component doesn't render anything
  return null;
};

export default GoogleAnalytics;
